import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  useDisclosure,
} from '@heroui/react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import React from 'react';
import useOfferComparisonStore, {
  selectSavingsAmount,
  selectTotalAnnualCostsInclPromo,
  selectTotalEnergyCostsInclPromo,
} from '../store/use-offer-comparison-store';
import useCustomerStore from '../store/use-customer-store';
import { EuroSignPosition, formatPrice } from '../utils/format-price';
import { useIsMobile } from '@/app/hooks/use-is-mobile';
import PriceInfoAccordion from './price-info-accordion';
import PriceInfoFinePrint from './price-info-fine-print';
import { useCustomizationContext } from '../customizations-provider';
import useEnergyCalculatorStore from '../store/use-energy-calculator-store';
import { CustomerType } from '@/lib/offer-service/types/customer-type';
import useVatLabel from '../utils/vat-label';

export default function PriceInfo() {
  const t = useTranslations('priceComparison');
  const isMobile = useIsMobile();
  const c = useCustomizationContext();
  const logo = c('tariff.logo');
  const { onOpen, isOpen, onOpenChange } = useDisclosure();
  const totalOfferInclDiscounts = useOfferComparisonStore(
    selectTotalEnergyCostsInclPromo
  );
  const offerWithGridCosts =
    useOfferComparisonStore(selectTotalAnnualCostsInclPromo) ?? 0;

  const savings = useOfferComparisonStore(selectSavingsAmount);
  const isGermanCustomer = useCustomerStore((state) => state.isGermanCustomer);
  const customerType = useEnergyCalculatorStore((state) => state.customerType);
  const isPrivateCustomer = customerType === CustomerType.Home;
  const vat = useVatLabel(isPrivateCustomer);
  const savingsStyles =
    'bg-[var(--color-span-on-highlight)] text-[var(--color-text-strong)] rounded-lg inline text-center px-2 py-1 md:py-3 my-3 md:my-0 mr-[2px]';
  const yearlyCostsSubtitle = `${t('totalPerYear')} ${vat}`;
  const totalOfferInHeader = isGermanCustomer
    ? formatPrice(totalOfferInclDiscounts, true, EuroSignPosition.Suffix)
    : `${formatPrice(totalOfferInclDiscounts, true)}`;

  const bannerTitle = t.rich('savings', {
    highlight: (chunks) => (
      <span data-testid="cost-info-savings-text" className={savingsStyles}>
        {chunks}
      </span>
    ),
    value: () => (
      <span data-testid="energyCalculatorTariffInfoSavingsValue">
        {formatPrice(savings, false)}&nbsp;
        {!isMobile && t('perYear')}
      </span>
    ),
  });

  return (
    <div>
      <Button
        data-testid="priceInfoTriggerButton"
        className="btn-secondary btn-padded min-w-full md:min-w-[209px] leading-6.5"
        onPress={onOpen}
      >
        <p data-testid="price-info-label">{t('moreInfo')}</p>
      </Button>
      <Modal
        placement="top"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        size="3xl"
        radius="lg"
        classNames={{
          closeButton:
            'custom-close-button !text-[var(--color-text-on-primary-action)] hover:bg-transparent/5 active:bg-transparent/10',
        }}
      >
        <ModalContent data-testid="energyCalculatorTariffInfoPopup">
          <ModalHeader
            className="bg-[var(--color-background-brand)] justify-start md:justify-between pt-8 md:pt-12 pb-6 md:pb-10 px-4 md:px-[126px]"
            data-testid="energyCalculatorTariffInfoPopupTitle"
          >
            <div className="flex flex-col text-[var(--color-text-on-primary-action)]">
              <Image
                data-testid="customer-logo"
                src={logo.url}
                alt={logo.title}
                height={40}
                width={163}
                className="pb-4"
              />
              <p
                data-testid="cost-info-title"
                className="md:hidden pb-2  font-heading leading-6.5"
              >
                {t('offerTariff')}
              </p>
              <p
                data-testid="energyCalculatorScreenOfferCardPrice"
                className="font-heading leading-[56px] text-[50px] pb-2"
              >
                {totalOfferInHeader}
              </p>
              {isGermanCustomer ? (
                <p data-testid="energyCalculatorIndividualOfferTariffOfferInfo">
                  {yearlyCostsSubtitle}
                </p>
              ) : (
                <p data-testid="energyCalculatorIndividualOfferTariffOfferInfo">{`${formatPrice(offerWithGridCosts, true)} ${t('includeGridCosts')}`}</p>
              )}
            </div>
            <p
              data-testid="tariffCardTitle"
              className="hidden md:block  font-heading leading-6.5 text-[var(--color-text-on-primary-action)]"
            >
              {t('offerTariff')}
            </p>
          </ModalHeader>
          <ModalBody className="p-0">
            {savings && savings > 0 && (
              <div
                className="bg-[var(--color-highlight-brand)] py-2 text-[var(--color-text-inverse)] text-center font-heading leading-[46px] text-xl"
                data-testid="energyCalculatorTariffInfoSavingsBanner"
              >
                {bannerTitle}
              </div>
            )}
            <div className="px-4 md:px-[126px]">
              <PriceInfoAccordion />
              <PriceInfoFinePrint isGerman={isGermanCustomer} />
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
}
